import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type { AttachmentResponse } from "../../../api/api";
import type { ProviderResponse } from "./providersApi";
import { addListTemplate, addProviderAttachment, deleteListTemplate, deleteProviderAttachment } from "./providersApi";

export const useProviderFiles = (providerId: string) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const addProviderAttachmentMutation = useMutation<AttachmentResponse, unknown, FormData>({
    mutationFn: (formData) => addProviderAttachment(providerId, formData),
    onSuccess: async () => {
      enqueueSnackbar("Załącznik został dodany", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["providers", providerId] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać załącznika. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const deletePackageAttachmentMutation = useMutation<AttachmentResponse[], unknown, { name: string }>({
    mutationFn: ({ name }) => deleteProviderAttachment(providerId, name),
    onSuccess: async () => {
      enqueueSnackbar("Załącznik został usunięty", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["providers", providerId] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć załącznika. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const addListTemplateMutation = useMutation<ProviderResponse, unknown, FormData>({
    mutationFn: (formData) => addListTemplate(providerId, formData),
    onSuccess: async (data) => {
      enqueueSnackbar("Wzór listy został dodany", { variant: "success" });
      await queryClient.setQueryData(["providers", providerId], data);
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać wzoru listy. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const deleteListTemplateMutation = useMutation<ProviderResponse, unknown, void>({
    mutationFn: () => deleteListTemplate(providerId),
    onSuccess: async (data) => {
      enqueueSnackbar("Wzór listy został usunięty", { variant: "success" });
      await queryClient.setQueryData(["providers", providerId], data);
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć wzoru listy. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  return {
    addAttachment: (formData: FormData) => addProviderAttachmentMutation.mutateAsync(formData),
    deleteAttachment: (name: string) => deletePackageAttachmentMutation.mutateAsync({ name }),
    addListTemplate: (formData: FormData) => addListTemplateMutation.mutateAsync(formData),
    deleteListTemplate: () => deleteListTemplateMutation.mutateAsync(),
  };
};
